/**
 * External dependencies
 */
import * as React from 'react';
import styled from 'styled-components';
import { useCallback, useState, useRef } from '@web-stories-wp/react';
import { __ } from '@web-stories-wp/i18n';
import { TextBorderLineCap } from '@src/web-stories-wp/story-editor/src/elements/types';
import {
  BUTTON_VARIANTS,
  BUTTON_SIZES,
  Icons,
  THEME_CONSTANTS,
  ToggleButton,
  useKeyDownEffect,
} from '@web-stories-wp/design-system';

/**
 * Internal dependencies
 */
import { useConfig } from '../../../../app';
import Tooltip from '../../../tooltip';
import { Label, type MultipleValue, SettingsRow } from './shared';
import { focusStyle } from '../../shared';

const StrokeLineCapRow = styled.div.attrs({
  role: 'listbox',
})`
  display: flex;
  align-items: center;
`;

const StrokeLineCapButton = styled(ToggleButton).attrs({
  variant: BUTTON_VARIANTS.SQUARE,
  size: BUTTON_SIZES.SMALL,
})`
  ${focusStyle}
`;

const strokeLineCapButtonIds = ['buttLineCap', 'squareLineCap', 'roundLineCap'];

type StrokeLineCapButtonId = typeof strokeLineCapButtonIds[number];

type TextBorderStrokeLineCapSettingsProps = {
  lineCap: TextBorderLineCap | MultipleValue;
  handleChange: (value: TextBorderLineCap) => void;
};

export function TextBorderStrokeLineCapSettings({
  lineCap,
  handleChange,
}: TextBorderStrokeLineCapSettingsProps) {
  const { isRTL } = useConfig();

  const rowRef = useRef<HTMLDivElement>();
  const [currentButton, setCurrentButton] =
    useState<StrokeLineCapButtonId | null>(null);

  const handleStrokeLineCapButtonNavigation = useCallback(
    (direction) => () => {
      const currentIndex = strokeLineCapButtonIds.findIndex(
        (id) => id === currentButton
      );
      const nextButtonId = strokeLineCapButtonIds[currentIndex + direction];
      if (!nextButtonId) {
        return;
      }
      setCurrentButton(nextButtonId);
      rowRef.current.querySelector(`#${nextButtonId}`).focus();
    },
    [currentButton, setCurrentButton]
  );

  useKeyDownEffect(
    rowRef,
    'left',
    () => handleStrokeLineCapButtonNavigation(isRTL ? 1 : -1),
    [handleStrokeLineCapButtonNavigation, isRTL]
  );

  useKeyDownEffect(
    rowRef,
    'right',
    () => handleStrokeLineCapButtonNavigation(isRTL ? -1 : 1),
    [handleStrokeLineCapButtonNavigation, isRTL]
  );

  return (
    <SettingsRow>
      <Label size={THEME_CONSTANTS.TYPOGRAPHY.PRESET_SIZES.SMALL}>
        {__('Dash cap', 'web-stories')}
      </Label>
      <StrokeLineCapRow ref={rowRef}>
        <Tooltip title={__('No dash cap', 'web-stories')}>
          <StrokeLineCapButton
            aria-label={__('No dash cap', 'web-stories')}
            aria-selected={lineCap === TextBorderLineCap.Butt}
            aria-pressed={lineCap === TextBorderLineCap.Butt}
            id={strokeLineCapButtonIds[0]}
            isToggled={lineCap === TextBorderLineCap.Butt}
            onClick={() => handleChange(TextBorderLineCap.Butt)}
            onFocus={() => setCurrentButton(strokeLineCapButtonIds[0])}
          >
            <Icons.StrokeLineCapButt />
          </StrokeLineCapButton>
        </Tooltip>
        <Tooltip title={__('Dash cap square', 'web-stories')}>
          <StrokeLineCapButton
            aria-label={__('Dash cap square', 'web-stories')}
            aria-selected={lineCap === TextBorderLineCap.Square}
            aria-pressed={lineCap === TextBorderLineCap.Square}
            id={strokeLineCapButtonIds[1]}
            isToggled={lineCap === TextBorderLineCap.Square}
            onClick={() => handleChange(TextBorderLineCap.Square)}
            onFocus={() => setCurrentButton(strokeLineCapButtonIds[1])}
          >
            <Icons.StrokeLineCapSquare />
          </StrokeLineCapButton>
        </Tooltip>
        <Tooltip title={__('Dash cap round', 'web-stories')}>
          <StrokeLineCapButton
            aria-label={__('Dash cap round', 'web-stories')}
            aria-selected={lineCap === TextBorderLineCap.Round}
            aria-pressed={lineCap === TextBorderLineCap.Round}
            id={strokeLineCapButtonIds[2]}
            isToggled={lineCap === TextBorderLineCap.Round}
            onClick={() => handleChange(TextBorderLineCap.Round)}
            onFocus={() => setCurrentButton(strokeLineCapButtonIds[2])}
          >
            <Icons.StrokeLineCapRound />
          </StrokeLineCapButton>
        </Tooltip>
      </StrokeLineCapRow>
    </SettingsRow>
  );
}
